export const CHAPTERS = [
  { number: 1, titleMarathi: 'अर्जुनविषादयोग', titleEnglish: "Arjuna's Despondency", titleSanskrit: 'Arjunavishadayogah' },
  { number: 2, titleMarathi: 'सांख्ययोग', titleEnglish: 'Samkhya Yoga', titleSanskrit: 'Samkhyayogah' },
  { number: 3, titleMarathi: 'कर्मयोग', titleEnglish: 'Karma Yoga', titleSanskrit: 'Karmayogah' },
  { number: 4, titleMarathi: 'ज्ञानकर्मसंन्यासयोग', titleEnglish: 'Knowledge and Renunciation', titleSanskrit: 'Jnanakarmasannyasayogah' },
  { number: 5, titleMarathi: 'कर्मसंन्यासयोग', titleEnglish: 'Renunciation of Action', titleSanskrit: 'Karmasannyasayogah' },
  { number: 6, titleMarathi: 'ध्यानयोग', titleEnglish: 'Meditation Yoga', titleSanskrit: 'Dhyanayogah' },
  { number: 7, titleMarathi: 'ज्ञानविज्ञानयोग', titleEnglish: 'Knowledge and Wisdom', titleSanskrit: 'Jnanavijnanayogah' },
  { number: 8, titleMarathi: 'अक्षरब्रह्मयोग', titleEnglish: 'Imperishable Brahman', titleSanskrit: 'Aksharabrahmayogah' },
  { number: 9, titleMarathi: 'राजविद्याराजगुह्ययोग', titleEnglish: 'Royal Knowledge', titleSanskrit: 'Rajavidyarajaguhyayogah' },
  { number: 10, titleMarathi: 'विभूतियोग', titleEnglish: 'Divine Glories', titleSanskrit: 'Vibhutiyogah' },
  { number: 11, titleMarathi: 'विश्वरूपदर्शनयोग', titleEnglish: 'Vision of the Universal Form', titleSanskrit: 'Vishvarupadarshanayogah' },
  { number: 12, titleMarathi: 'भक्तियोग', titleEnglish: 'Devotion Yoga', titleSanskrit: 'Bhaktiyogah' },
  { number: 13, titleMarathi: 'क्षेत्रक्षेत्रज्ञविभागयोग', titleEnglish: 'Field and Knower', titleSanskrit: 'Kshetrakshetrajnavibhagayogah' },
  { number: 14, titleMarathi: 'गुणत्रयविभागयोग', titleEnglish: 'Three Gunas', titleSanskrit: 'Gunatrayavibhagayogah' },
  { number: 15, titleMarathi: 'पुरुषोत्तमयोग', titleEnglish: 'Supreme Person', titleSanskrit: 'Purushottamayogah' },
  { number: 16, titleMarathi: 'दैवासुरसंपद्विभागयोग', titleEnglish: 'Divine and Demoniac', titleSanskrit: 'Daivasurasampadvibhagayogah' },
  { number: 17, titleMarathi: 'श्रद्धात्रयविभागयोग', titleEnglish: 'Three Kinds of Faith', titleSanskrit: 'Shraddhatrayavibhagayogah' },
  { number: 18, titleMarathi: 'मोक्षसंन्यासयोग', titleEnglish: 'Liberation through Renunciation', titleSanskrit: 'Mokshasannyasayogah' },
] as const;

export const APP_NAME = 'Dnyaneshwari';
export const APP_NAME_MARATHI = 'ज्ञानेश्वरी';
export const APP_VERSION = '1.0.0';

/** Marathi first: it is the default and the language the scripture is in. */
export const SUPPORTED_LANGUAGES = ['mr', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const THEMES = ['light', 'dark', 'sepia'] as const;
export type Theme = (typeof THEMES)[number];

export const FONT_SIZE_RANGE = { min: 14, max: 28, default: 18 } as const;

export const COLLECTIONS = {
  CHAPTERS: 'chapters',
  OVIS: 'ovis',
  SHLOKAS: 'shlokas',
  USERS: 'users',
  EVENTS: 'events',
  DAILY_VERSES: 'dailyVerses',
  NOTIFICATIONS: 'notifications',
  DAILY_AGGREGATES: 'dailyAggregates',
  USER_DAILY_ACTIVITY: 'userDailyActivity',
  ADMINS: 'admins',
  /** Small admin-editable config docs, e.g. settings/dailyVerse */
  SETTINGS: 'settings',
  /** Proofreading edits to the static scripture bundle; applied by scripts/apply-corrections.ts */
  TEXT_CORRECTIONS: 'textCorrections',
  /**
   * भावार्थ (plain-Marathi explanation) per ovi, one doc per chapter:
   * meanings/{chapterId} with items keyed by zero-padded ovi number. Lives in
   * Firestore, not the static bundle, so it can be added from the admin at
   * any time and shows in the app without a release.
   */
  MEANINGS: 'meanings',
  /**
   * ज्ञानकिरण cards, one doc each, written only from the admin. The app never
   * reads this collection: it reads content/dnyankiranToday, the one card
   * currently published, which the publish function writes.
   */
  DNYANKIRAN: 'dnyankiran',
  /**
   * oviText/{chapterId} with items keyed by zero-padded ovi number: a
   * correction to a verse as the printed book has it. The bundled text ships
   * with the app; anything here replaces it, so a wrong verse can be put
   * right without a release.
   */
  OVI_TEXT: 'oviText',
  /** Per-chapter proofreading position: PDF page and last verified ovi */
  REVIEW_PROGRESS: 'reviewProgress',
  /** Subcollection under a chapter: chapters/{id}/parts */
  PARTS: 'parts',
  CONTENT: 'content',
} as const;

/**
 * Ovis per chapter as printed on the contents page of the Gita Press
 * पारायण प्रत (code 859) -- 9,035 in all. A fixed fact of the book, used to
 * validate an ovi reference without loading the text.
 */
export const CHAPTER_OVI_COUNTS = [
  275, 375, 276, 225, 180, 497, 210, 271, 535, 335, 708, 247, 1170, 415, 599, 473, 433, 1811,
] as const;

/**
 * Gita Press edition (code 859): PDF page on which each chapter begins, from
 * the book's contents page. Printed page numbers equal PDF page indexes.
 */
export const CHAPTER_START_PAGES = [9, 26, 51, 68, 83, 95, 124, 138, 155, 187, 209, 255, 268, 323, 343, 374, 398, 420] as const;
export const BOOK_PAGE_COUNT = 512;
/** Storage folder holding the rendered book pages, admin-only: review/pages/NNN.jpg */
export const REVIEW_PAGES_PATH = 'review/pages';

/** settings/{DAILY_VERSE_SETTINGS_DOC} holds the daily-verse send time. */
export const DAILY_VERSE_SETTINGS_DOC = 'dailyVerse';
/** Default push time for the daily verse, HH:mm in Asia/Kolkata. */
export const DEFAULT_DAILY_VERSE_TIME = '05:30';

/** Where the published scripture bundle (manifest + chapters/NN.json) is served from. */
export const CONTENT_PUBLIC_URL = 'https://dnyaneshwari-mobile.web.app/content';

/** Public privacy policy (English + Marathi), served as a static page from mobile hosting. */
export const PRIVACY_POLICY_URL = 'https://shridnyaneshwari.com/privacy/';

export const STORAGE_PATHS = {
  AUDIO: 'audio',
  /** Master recordings as uploaded; the streaming copy is made from these. */
  AUDIO_SOURCE: 'audio-src',
  VIDEO: 'video',
  IMAGES: 'images',
  CHAPTER_COVERS: 'images/chapters',
  /** ज्ञानकिरण illustrations and share cards; public read like the rest of images/. */
  DNYANKIRAN: 'images/dnyankiran',
  /** Full-screen promotion artwork; public read like the rest of images/. */
  PROMOTIONS: 'images/promotions',
  SLIDE_BACKGROUNDS: 'images/slides',
  USER_PROFILES: 'users',
} as const;

export const FCM_TOPICS = {
  DAILY_VERSE: 'daily-verse',
  ANNOUNCEMENTS: 'announcements',
} as const;

/** content/stories: the live copy of stories.json the admin publishes. */
export const STORIES_DOC = 'stories';

/** content/dnyankiranToday: the one ज्ञानकिरण card currently published. */
export const DNYANKIRAN_TODAY_DOC = 'dnyankiranToday';
/** settings/dnyankiran: { sendTime } — when a scheduled card goes out. */
export const DNYANKIRAN_SETTINGS_DOC = 'dnyankiran';

/**
 * content/promotion -- the one full-screen promotion the app shows on open.
 * A single document, like dnyankiranToday: the app never queries a
 * collection, so the check costs one read however many promotions have been
 * run. Clearing the document turns the feature off.
 */
export const PROMOTION_DOC = 'promotion';

/**
 * content/reviews -- what people have written about the app, gathered by hand
 * from WhatsApp, the stores, email and conversation, and shown on the website.
 *
 * One document holding the lot, like the stories: the site reads it with a
 * single public GET and no SDK, so adding a review costs nothing and needs no
 * deploy.
 */
export const REVIEWS_DOC = 'reviews';
/** Separate from आजची ओवी's 05:30 so the two pushes never land together. */
export const DEFAULT_DNYANKIRAN_TIME = '07:00';
/** The FCM topic every registered device is subscribed to for the cards. */
export const DNYANKIRAN_TOPIC = 'dnyankiran';

/** The eight ज्ञानकिरण categories, in the order of the first set of cards. */
export const DNYANKIRAN_CATEGORIES = [
  { id: 'khagol', mr: 'खगोल', en: 'Astronomy' },
  { id: 'vidnyan', mr: 'विज्ञान', en: 'Science' },
  { id: 'nisarg', mr: 'निसर्ग', en: 'Nature' },
  { id: 'tantradnyan', mr: 'तंत्रज्ञान', en: 'Technology' },
  { id: 'arogya', mr: 'आरोग्य', en: 'Health' },
  { id: 'man', mr: 'मन', en: 'Mind' },
  { id: 'sanskruti', mr: 'संस्कृती', en: 'Culture' },
  { id: 'jivanmulye', mr: 'जीवनमूल्ये', en: 'Values' },
] as const;
export type DnyankiranCategoryId = (typeof DNYANKIRAN_CATEGORIES)[number]['id'];

/** content/meaningsVersions: { versions: { "01": <ms> } }, stamped by the admin on every भावार्थ save. */
export const MEANINGS_VERSIONS_DOC = 'meaningsVersions';

/** content/oviTextVersions: the same, stamped whenever a chapter's verses are corrected. */
export const OVI_TEXT_VERSIONS_DOC = 'oviTextVersions';
